import { Button } from "@sum-video/shared/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@sum-video/shared/components/ui/sheet";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import { LogOut, Menu, Settings, User } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { LocaleSwitcher } from "../LocaleSwitcher";
import { ThemeSwitcher } from "../ThemeSwitcher";

interface MobileMenuProps {
  t: (key: string) => string;
  user: SupabaseUser | null;
  loading: boolean;
  isSubscribed: boolean;
  onLogout: () => void;
}

export function MobileMenu({
  t,
  user,
  loading,
  isSubscribed,
  onLogout,
}: MobileMenuProps) {
  const sectionsT = useTranslations("BlogSections");
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden">
          <Menu className="h-6 w-6" />
          <span className="sr-only">{t("toggleMenu")}</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-80">
        <SheetHeader>
          <SheetTitle>{t("brand")}</SheetTitle>
        </SheetHeader>
        <nav className="flex flex-col space-y-4 mt-6">
          <Button variant="ghost" className="justify-start" asChild>
            <Link href="/how-it-works">{t("howItWorks")}</Link>
          </Button>
          <Button variant="ghost" className="justify-start" asChild>
            <Link href="/guides">{sectionsT("guides.name")}</Link>
          </Button>
          <Button variant="ghost" className="justify-start" asChild>
            <Link href="/comparisons">{sectionsT("comparisons.name")}</Link>
          </Button>
          <Button variant="ghost" className="justify-start" asChild>
            <Link href="/affiliate">{t("affiliate")}</Link>
          </Button>
          {(!user || !isSubscribed) && (
            <Button variant="ghost" className="justify-start" asChild>
              <Link href="/pricing">{t("pricing")}</Link>
            </Button>
          )}
          <div className="flex items-center justify-between pt-4 border-t">
            <ThemeSwitcher />
            <LocaleSwitcher />
          </div>
          <div className="flex flex-col space-y-2 pt-4 border-t">
            {loading ? (
              <div className="h-8 w-32 bg-muted animate-pulse rounded mx-4" />
            ) : user ? (
              <>
                <div className="flex items-center space-x-2 px-4 py-2">
                  <div className="h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                    <User className="h-3 w-3 text-primary-foreground" />
                  </div>
                  <span className="text-base font-medium text-foreground">
                    {user.email}
                  </span>
                </div>
                <Button variant="outline" className="justify-start" asChild>
                  <Link href="/wisdom">{t("wisdom")}</Link>
                </Button>
                <Button variant="outline" className="justify-start" asChild>
                  <Link href="/dashboard/library">{t("library")}</Link>
                </Button>
                <Button variant="outline" className="justify-start" asChild>
                  <Link href="/dashboard/subscriptions">
                    {t("subscriptions")}
                  </Link>
                </Button>
                <Button variant="outline" className="justify-start" asChild>
                  <Link href="/dashboard">
                    <Settings className="mr-2 h-4 w-4" />
                    {t("dashboard")}
                  </Link>
                </Button>
                <Button
                  variant="destructive"
                  className="justify-start"
                  onClick={onLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("signOut")}
                </Button>
              </>
            ) : (
              <Button className="justify-start" asChild>
                <Link href="/auth">
                  <User className="mr-2 h-4 w-4" />
                  {t("getStarted")}
                </Link>
              </Button>
            )}
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
